import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Text from "../components/atoms/Text";
import products from "../data/products.json";
import { countryOptions, cityOptionsByCountry } from "../data/locationOptions";
import { useUser } from "../context/UserContext";
import { useNotification } from "../context/NotificationContext";
import SidebarProfile from "../components/organisms/SidebarProfile";
import DashboardProfile from "../components/organisms/DashboardProfile";
import OrderHistoryProfile from "../components/organisms/OrderHistoryProfile";
import OrderDetailsProfile from "../components/organisms/OrderDetailsProfile";
import WishlistProfile from "../components/organisms/WishlistProfile";
import CartProfile from "../components/organisms/CartProfile";
import SettingsProfile from "../components/organisms/SettingsProfile";
import {
  fallbackOrders,
  navigationItems,
  splitFullName,
} from "../components/organisms/ProfileShared";

export default function Profile() {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const {
    currentUser,
    isAuthenticated,
    isLoadingUser,
    logout,
    updateCartQuantity,
    removeFromCart,
    updateUserDetails,
    addToCart,
    toggleWishlist,
  } = useUser();

  const [activeSection, setActiveSection] = useState("dashboard");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [settingsFocus, setSettingsFocus] = useState("account");
  const fileInputRef = useRef(null);
  const [accountForm, setAccountForm] = useState({
    firstName: "",
    lastName: "",
    displayName: "",
    email: "",
    phone: "",
  });
  const [billingForm, setBillingForm] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    address: "",
    country: "",
    city: "",
    zipCode: "",
    email: "",
    phone: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const resolvedOrders = useMemo(() => {
    const userOrders = currentUser?.orderHistory?.length
      ? currentUser.orderHistory.map((order) => ({
          ...order,
          id: order.id?.toString().startsWith("#") ? order.id : `#${order.id}`,
          date: order.date || "24 Apr, 2021",
          paymentMethod: order.paymentMethod || "Paypal",
          discountRate: order.discountRate || 0.2,
          billingAddress:
            order.billingAddress || {
              name: currentUser?.name || "Customer",
              address: "4140 Parker Rd. Allentown, New Mexico 31134",
              email: currentUser?.email || "customer@email.com",
              phone: "254 555-0110",
            },
          shippingAddress:
            order.shippingAddress || {
              name: currentUser?.name || "Customer",
              address: "4140 Parker Rd. Allentown, New Mexico 31134",
              email: currentUser?.email || "customer@email.com",
              phone: "254 555-0110",
            },
        }))
      : fallbackOrders;

    return userOrders;
  }, [currentUser]);

  const selectedOrder =
    resolvedOrders.find((order) => order.id === selectedOrderId) ||
    resolvedOrders[0];
  const paginatedOrders = resolvedOrders.slice(
    (currentPage - 1) * 10,
    currentPage * 10,
  );
  const pageCount = Math.max(1, Math.ceil(resolvedOrders.length / 10));
  const wishlistProducts = products.filter((product) =>
    currentUser?.wishlist?.includes(product.id),
  );
  const cartProducts = (currentUser?.cart || [])
    .map((item) => {
      const product = products.find((entry) => entry.id === item.productId);

      if (!product) {
        return null;
      }

      return {
        ...product,
        quantity: item.quantity,
      };
    })
    .filter(Boolean);

  useEffect(() => {
    const { firstName, lastName } = splitFullName(currentUser?.name || "");
    const billingAddress = currentUser?.billingAddress || {};

    setAccountForm({
      firstName,
      lastName,
      displayName: currentUser?.name || "",
      email: currentUser?.email || "",
      phone: billingAddress.phone || "",
    });

    setBillingForm({
      firstName: billingAddress.firstName || firstName,
      lastName: billingAddress.lastName || lastName,
      companyName: billingAddress.companyName || "",
      address: billingAddress.address || "",
      country: billingAddress.country || "",
      city: billingAddress.city || billingAddress.state || "",
      zipCode: billingAddress.zipCode || "",
      email: billingAddress.email || currentUser?.email || "",
      phone: billingAddress.phone || "",
    });
  }, [currentUser]);

  async function handleNavigationClick(sectionKey) {
    if (sectionKey === "logout") {
      await logout();
      navigate("/auth");
      return;
    }

    if (sectionKey === "settings") {
      setSettingsFocus("account");
    }

    setActiveSection(sectionKey);
  }

  function openSettingsSection(section) {
    setSettingsFocus(section);
    setActiveSection("settings");
  }

  async function handleAccountSave() {
    const fullName = `${accountForm.firstName} ${accountForm.lastName}`.trim();
    const normalizedEmail = accountForm.email.trim().toLowerCase();

    if (!accountForm.firstName.trim() || !normalizedEmail) {
      notify("Please complete the required account fields.", "error");
      return;
    }

    await updateUserDetails(
      {
        name: accountForm.displayName.trim() || fullName,
        email: normalizedEmail,
        billingAddress: {
          ...(currentUser?.billingAddress || {}),
          phone: accountForm.phone.trim(),
          email: currentUser?.billingAddress?.email || normalizedEmail,
          firstName:
            currentUser?.billingAddress?.firstName ||
            accountForm.firstName.trim(),
          lastName:
            currentUser?.billingAddress?.lastName ||
            accountForm.lastName.trim(),
        },
      },
      "Account settings updated.",
    );
  }

  async function handleBillingSave() {
    await updateUserDetails(
      {
        billingAddress: {
          firstName: billingForm.firstName.trim(),
          lastName: billingForm.lastName.trim(),
          companyName: billingForm.companyName.trim(),
          address: billingForm.address.trim(),
          country: billingForm.country.trim(),
          city: billingForm.city.trim(),
          zipCode: billingForm.zipCode.trim(),
          email: billingForm.email.trim().toLowerCase(),
          phone: billingForm.phone.trim(),
        },
      },
      "Billing address updated.",
    );
  }

  async function handlePasswordSave() {
    if (
      !passwordForm.currentPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      notify("Please complete all password fields.", "error");
      return;
    }

    if (passwordForm.currentPassword !== currentUser?.password) {
      notify("Current password is incorrect.", "error");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      notify("New passwords do not match.", "error");
      return;
    }

    await updateUserDetails(
      {
        password: passwordForm.newPassword,
      },
      "Password updated.",
    );

    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  }

  function handleProfileImageChange(event) {
    event.preventDefault();
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const nextPfp = typeof reader.result === "string" ? reader.result : "";

      if (!nextPfp) {
        return;
      }

      await updateUserDetails(
        {
          pfp: nextPfp,
        },
        "Profile image updated.",
      );
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  }

  function renderContent() {
    const billing =
      currentUser?.billingAddress ||
      selectedOrder?.billingAddress || {
        name: currentUser?.name || "Customer",
        address: "4140 Parker Rd. Allentown, New Mexico 31134",
        email: currentUser?.email || "customer@email.com",
        phone: "254 555-0110",
      };

    if (activeSection === "dashboard") {
      return (
        <DashboardProfile
          currentUser={currentUser}
          billing={billing}
          recentOrders={resolvedOrders.slice(0, 6)}
          onOpenSettingsSection={openSettingsSection}
          onViewAllOrders={() => setActiveSection("orders")}
          onSelectOrder={(orderId) => {
            setSelectedOrderId(orderId);
            setActiveSection("order-details");
          }}
        />
      );
    }

    if (activeSection === "orders") {
      return (
        <OrderHistoryProfile
          orders={paginatedOrders}
          showPagination
          currentPage={currentPage}
          pageCount={pageCount}
          onPageChange={setCurrentPage}
          onSelectOrder={(orderId) => {
            setSelectedOrderId(orderId);
            setActiveSection("order-details");
          }}
        />
      );
    }

    if (activeSection === "order-details") {
      return (
        <OrderDetailsProfile
          selectedOrder={selectedOrder}
          onBackToOrders={() => setActiveSection("orders")}
        />
      );
    }

    if (activeSection === "wishlist") {
      return (
        <WishlistProfile
          wishlistProducts={wishlistProducts}
          onAddToCart={addToCart}
          onToggleWishlist={toggleWishlist}
        />
      );
    }

    if (activeSection === "cart") {
      return (
        <CartProfile
          cartProducts={cartProducts}
          onUpdateCartQuantity={updateCartQuantity}
          onRemoveFromCart={removeFromCart}
        />
      );
    }

    if (activeSection === "settings") {
      return (
        <SettingsProfile
          currentUser={currentUser}
          settingsFocus={settingsFocus}
          fileInputRef={fileInputRef}
          accountForm={accountForm}
          setAccountForm={setAccountForm}
          billingForm={billingForm}
          setBillingForm={setBillingForm}
          passwordForm={passwordForm}
          setPasswordForm={setPasswordForm}
          cityOptions={cityOptionsByCountry[billingForm.country] || []}
          countryOptions={countryOptions}
          onProfileImageChange={handleProfileImageChange}
          onAccountSave={handleAccountSave}
          onBillingSave={handleBillingSave}
          onPasswordSave={handlePasswordSave}
        />
      );
    }

    return null;
  }

  if (isLoadingUser) {
    return (
      <div
        className="profile-page profile-guest"
        style={{ padding: "60px 300px 100px", backgroundColor: "var(--white)" }}
      >
        <Text size="px32" weight="w600" color="gray9">
          Loading your dashboard...
        </Text>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div
        className="profile-page profile-guest"
        style={{ padding: "60px 300px 100px", backgroundColor: "var(--white)" }}
      >
        <Text size="px32" weight="w600" color="gray9">
          Please sign in to view your dashboard.
        </Text>
      </div>
    );
  }

  return (
    <div
      className="profile-page"
      style={{ padding: "40px 300px 100px", backgroundColor: "var(--white)" }}
    >
      <div
        className="profile-layout"
        style={{
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          gap: "24px",
          alignItems: "start",
        }}
      >
        <SidebarProfile
          navigationItems={navigationItems}
          activeSection={activeSection}
          onNavigate={handleNavigationClick}
        />
        {renderContent()}
      </div>
    </div>
  );
}
